import React from "react";
import { Link } from 'react-router-dom';
// Icons
import IconDownload from "../iconComponents/IconDownload";
// components
import Warning from "../common/Warning";
// Helpers
import { numberWithCommas, formatDateTimeToZone } from "../common/Helpers";

// Shared, read-only rendering of an invoice's "Details" section -- used both by the
// live invoice page and the past-version modal, so a previous version renders identically
// to the current one (just with that version's own field values and attachments).
export default function InvoiceDetailsCard({ invoice, downloadAttachment, historyVersionsControls }) {

    const attachmentsLength = invoice.attachments ? invoice.attachments.length : 0;
    const attachmentsLengthAdditional = invoice.attachments_additional ? invoice.attachments_additional.length : 0;

    return (
        <>
            {invoice.is_stage_rejected &&
            <Warning className="gap-below-m gap-above-m" >
                This invoice was pushed back to the current stage on {formatDateTimeToZone(invoice.rejected_date)} with the following comments: <br />
                <div className="heavy tb-text">"{invoice.rejected_comments}"
                    <span className="light tb-text">&nbsp;&nbsp;— {invoice.rejected_by_email}</span>
                </div>
            </Warning>}
            <div className="doc-section">
            <h3 className="title2 heavy label">Details</h3>
                <div className="info-grid c-5 r-4">
                    <div className="item firstcol">
                        <h3 className="content2 heavy-grey">From</h3>
                        {invoice.company_vendor && !invoice.company_vendor.is_basic &&
                        <h4 className="content1 light-black">
                            <img className="gap-right-s list-logo iblock" src={invoice.company_vendor.company_icon_path} role="presentation" />
                            {invoice.company_vendor.name}
                        </h4>}
                        <h5 className="content2 light-black multiline" style={{ whiteSpace: "break-spaces" }}>{invoice.VCA && (invoice.VCA.first_name + "  " + invoice.VCA.last_name)}</h5>
                        {invoice.company_vendor && invoice.company_vendor.is_basic &&
                        <h5 className="content2 light-black multiline" style={{ whiteSpace: "break-spaces" }}>{invoice.VCA && invoice.VCA.email}</h5>}
                    </div>
                    <div className="item">
                        <h3 className="content2 heavy-grey">To</h3>
                        {invoice.company_purchaser && !invoice.company_purchaser.is_basic &&
                        <h4 className="content1 light-black">
                            <img className="gap-right-s list-logo iblock" src={invoice.company_purchaser.company_icon_path} role="presentation" />
                            {invoice.company_purchaser.name}
                        </h4>}
                        <h5 className="content2 light-black multiline" style={{ whiteSpace: "break-spaces" }}>{invoice.PCA && (invoice.PCA.first_name + "  " + invoice.PCA.last_name)}</h5>
                        {invoice.company_purchaser && invoice.company_purchaser.is_basic &&
                        <h5 className="content2 light-black multiline" style={{ whiteSpace: "break-spaces" }}>{invoice.PCA && invoice.PCA.email}</h5>}
                    </div>
                    <div className="item ">
                        <h3 className="content2 heavy-grey">Contract</h3>
                        <Link to={`/mng/workflow/view/${invoice.contract_id}`} className="link2">
                            <h4 className="content1 light-black">{invoice.contract_name}</h4>
                            <h5 className="content2 light-black multiline" style={{ whiteSpace: "break-spaces" }}>{invoice.contract_number}</h5>
                        </Link>
                    </div>
                    <div className="item" style={{ gridColumn: "4 / span 2" }}>
                        <h3 className="content2 heavy-grey" >Project</h3>
                        <Link to={`/mng/project/view/${invoice.project_id}`} className="content1 link2">{invoice.project_name}</Link>
                    </div>
                    <div className="item firstcol">
                        <h3 className="content2 heavy-grey">Cost Code</h3>
                        {invoice.costcode
                        ?<h4 className="content1 light-black">{invoice.costcode}</h4>
                        :<h4 className="content1 g7-text">Not included</h4>}
                    </div>
                    <div className="item">
                        <h3 className="content2 heavy-grey">PO#</h3>
                        {invoice.po
                        ?<h4 className="content1 light-black">{invoice.po}</h4>
                        :<h4 className="content1 g7-text">Not included</h4>}
                    </div>
                    <div className="item ">
                        <h3 className="content2 heavy-grey">Subtotal</h3>
                        {invoice.subtotal
                        ?<h4 className="content1 light-black">{numberWithCommas(invoice.subtotal)}</h4>
                        :<h4 className="content1 g7-text">Not included</h4>}
                    </div>
                    <div className="item" >
                        <h3 className="content2 heavy-grey">Taxes</h3>
                        {invoice.taxes
                        ?<h4 className="content1 light-black">{numberWithCommas(invoice.taxes)}</h4>
                        :<h4 className="content1 g7-text">Not included</h4>}
                    </div>
                    <div className="item" >
                        <h3 className="content2 heavy-grey">Total</h3>
                        {invoice.total_value
                        ?<h4 className="content1 heavy">$ {numberWithCommas(invoice.total_value)}</h4>
                        :<h4 className="content1 g7-text">Not included</h4>}
                    </div>
                    <div className="item firstcol lastrow" >
                        <h3 className="content2 heavy-grey">Invoice Documents</h3>
                        {attachmentsLength > 0
                        ?<ul className="dropped gap-below-m">
                            {invoice.attachments.map((file) => {
                                return (
                                    <li key={file.id}>
                                        <a onClick={() => downloadAttachment(file.id, file.file_name)}>
                                            <IconDownload className="iblock" height="13px" pathClass="p-fill" /> &nbsp;
                                        </a>
                                        <p className="iblock content1 light-black">{file.file_name} </p>
                                    </li>
                                )
                            })}
                        </ul>
                        :<h4 className="content1 g7-text">Not included</h4>}
                    </div>
                    <div className="item lastrow" >
                        <h3 className="content2 heavy-grey">Supporting Documents</h3>
                        {attachmentsLengthAdditional > 0
                        ?<ul className="dropped gap-below-m">
                            {invoice.attachments_additional.map((file) => {
                                return (
                                    <li key={file.id}>
                                        <a onClick={() => downloadAttachment(file.id, file.file_name)}>
                                            <IconDownload className="iblock" height="13px" pathClass="p-fill" /> &nbsp;
                                        </a>
                                        <p className="iblock content1 light-black">{file.file_name} </p>
                                    </li>
                                );
                            })}
                            </ul>
                        :<h4 className="content1 g7-text">Not included</h4>}
                    </div>
                    {historyVersionsControls &&
                    <div className="item lastrow" style={{ gridColumn: "3 / span 3" }} >
                        {historyVersionsControls}
                    </div>}
                </div>
            </div>
        </>
    );
}
